import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { TestingView } from "../components/exam/TestingView";
import { StudyView } from "../components/exam/StudyView";
import { ReviewScreen } from "../components/exam/ReviewScreen";
import { BreakScreen } from "../components/exam/BreakScreen";

export default function ExamRunner() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [es, setEs] = useState(null);
  const [qs, setQs] = useState({});
  const [view, setView] = useState("question");
  const [walk, setWalk] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [endSectionOpen, setEndSectionOpen] = useState(false);
  const [breakLeft, setBreakLeft] = useState(null);
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const goToReport = useCallback(() => navigate(`/report/${sessionId}`, { replace: true }), [navigate, sessionId]);

  useEffect(() => {
    api.get(`/exam-sessions/${sessionId}`)
      .then(({ data }) => {
        if (data.status === "completed") return goToReport();
        setEs(data);
        setTimeLeft(data.time_remaining_seconds);
        setBreakLeft(data.break_remaining_seconds ?? null);
      })
      .catch((e) => {
        toast.error(apiError(e, "Could not load exam session"));
        navigate("/", { replace: true });
      });
  }, [sessionId, goToReport, navigate]);

  const pos = es?.current_position || 1;
  const posKey = String(pos);
  const q = qs[pos];
  const secLo = es?.section_start ?? 1;
  const secHi = es?.section_end ?? (es?.total_questions ?? 180);

  useEffect(() => {
    if (!es || es.on_break || qs[pos]) return;
    api.get(`/exam-sessions/${sessionId}/questions/${pos}`)
      .then(({ data }) => {
        if (data.session_status === "completed") return goToReport();
        setQs((prev) => ({ ...prev, [pos]: data }));
        if (data.time_remaining_seconds != null) setTimeLeft(data.time_remaining_seconds);
      })
      .catch((e) => {
        if (e.response?.status === 429) toast.error("Slow down — question fetch limit reached");
        else toast.error(apiError(e, "Could not load question"));
      });
  }, [es, pos, qs, sessionId, goToReport]);

  const doSubmit = useCallback(async (auto = false) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/exam-sessions/${sessionId}/submit`);
      if (auto) toast.warning("Time expired — your exam was submitted automatically");
      goToReport();
    } catch (e) {
      submittingRef.current = false;
      setSubmitting(false);
      toast.error(apiError(e, "Submit failed — retrying is safe"));
    }
  }, [sessionId, goToReport]);

  useEffect(() => {
    if (!es || es.mode !== "testing" || timeLeft == null) return;
    if (es.on_break && breakLeft != null && breakLeft > 0) return; // clock paused during break
    if (timeLeft <= 0) { doSubmit(true); return; }
    const t = setTimeout(() => setTimeLeft((s) => (s == null ? s : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, es, doSubmit, breakLeft]);

  useEffect(() => {
    if (!es?.on_break || breakLeft == null || breakLeft <= 0) return;
    const t = setTimeout(() => setBreakLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [breakLeft, es]);

  const patch = useCallback(async (path, body) => {
    try {
      const { data } = await api.patch(`/exam-sessions/${sessionId}/${path}`, body);
      if (data.time_remaining_seconds != null) setTimeLeft(data.time_remaining_seconds);
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail;
        if (d?.status === "completed" || d === "Exam session already completed") return goToReport();
        toast.error(apiError(e));
      } else {
        toast.error("Save failed — check your connection");
      }
    }
  }, [sessionId, goToReport]);

  const setAnswer = (idx) => {
    if (es.mode === "study" && es.checked?.[posKey]) return;
    setEs((p) => ({ ...p, answers: { ...p.answers, [posKey]: idx } }));
    patch("answer", { question_number: pos, answer_index: idx });
  };

  const toggleMark = () => {
    const isMarked = es.marked.includes(pos);
    setEs((p) => ({ ...p, marked: isMarked ? p.marked.filter((n) => n !== pos) : [...p.marked, pos] }));
    patch("mark", { question_number: pos, marked: !isMarked });
  };

  const setHighlights = (ranges) => {
    setEs((p) => ({ ...p, highlights: { ...p.highlights, [posKey]: ranges } }));
    patch("highlight", { question_number: pos, ranges });
  };

  const toggleStrike = (optIdx) => {
    const cur = es.strikeouts?.[posKey] || [];
    const next = cur.includes(optIdx) ? cur.filter((i) => i !== optIdx) : [...cur, optIdx];
    setEs((p) => ({ ...p, strikeouts: { ...p.strikeouts, [posKey]: next } }));
    patch("strikeout", { question_number: pos, option_indexes: next });
  };

  const goTo = (n, keepWalk = false) => {
    if (n < secLo || n > secHi) return;
    if (!keepWalk) setWalk(null);
    setEs((p) => ({ ...p, current_position: n }));
    setView("question");
    patch("position", { position: n });
  };

  const walkIndex = walk ? walk.list.indexOf(pos) : -1;
  const stateRef = useRef({});
  const nextRef = useRef(() => {});
  useEffect(() => {
    // ENTER records the selected answer and advances (item: keyboard-driven pacing for 180 questions)
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      const { es: cur, pos: p, view: v } = stateRef.current;
      if (!cur || v !== "question") return;
      const tag = e.target?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT") return;
      if (cur.answers?.[String(p)] != null) {
        e.preventDefault();
        nextRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  stateRef.current = { es, pos, view };
  const next = () => {
    if (walk && walkIndex >= 0) {
      if (walkIndex + 1 < walk.list.length) goTo(walk.list[walkIndex + 1], true);
      else { setWalk(null); setView("review"); }
    } else goTo(pos + 1);
  };
  nextRef.current = next;
  const prev = () => {
    if (walk && walkIndex > 0) goTo(walk.list[walkIndex - 1], true);
    else if (!walk) goTo(pos - 1);
  };

  const startWalk = (type) => {
    const all = Array.from({ length: secHi - secLo + 1 }, (_, i) => secLo + i);
    const answered = new Set(Object.keys(es.answers).map(Number));
    const list =
      type === "all" ? all :
      type === "marked" ? [...es.marked].filter((n) => n >= secLo && n <= secHi).sort((a, b) => a - b) :
      all.filter((n) => !answered.has(n));
    if (list.length === 0) return toast.info(`No ${type === "marked" ? "marked" : "incomplete"} questions`);
    const labels = { all: "Reviewing All", marked: "Reviewing Marked", incomplete: "Reviewing Incomplete" };
    setWalk({ label: labels[type], list });
    goTo(list[0], true);
  };

  const endSection = async () => {
    setEndSectionOpen(false);
    try {
      const { data } = await api.post(`/exam-sessions/${sessionId}/end-section`);
      setEs(data);
      setBreakLeft(data.break_remaining_seconds ?? null);
      setWalk(null);
      setView("question");
    } catch (e) {
      toast.error(apiError(e, "Could not end section"));
    }
  };

  const resumeSection = async () => {
    try {
      const { data } = await api.post(`/exam-sessions/${sessionId}/resume-section`);
      setEs(data);
      setTimeLeft(data.time_remaining_seconds);
      setBreakLeft(null);
      setView("question");
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.status === "completed") return goToReport();
      toast.error(apiError(e, "Could not resume the exam"));
    }
  };

  const doCheck = async () => {
    try {
      const { data } = await api.post(`/exam-sessions/${sessionId}/check`, { question_number: pos });
      setQs((p) => ({ ...p, [pos]: { ...p[pos], correct_index: data.correct_index, explanation: data.explanation } }));
      setEs((p) => ({ ...p, checked: { ...p.checked, [posKey]: { is_correct: data.is_correct } } }));
    } catch (e) {
      toast.error(apiError(e, "Could not check answer"));
    }
  };

  if (!es) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 text-sm font-medium tracking-wide">Loading exam…</div>
      </div>
    );
  }

  const answeredCount = Object.keys(es.answers).length;
  const shared = {
    es, q, pos, timeLeft, walk, walkIndex,
    setAnswer, toggleMark, setHighlights, toggleStrike,
    next, prev, goTo, doCheck,
    openReview: () => setView("review"),
    openSubmit: () => setConfirmOpen(true),
    exitWalk: () => { setWalk(null); setView("review"); },
    exit: () => {
      if (es.mode === "testing" && !window.confirm("Exit this exam? Your progress is saved and the timer keeps running — resume from the dashboard.")) return;
      navigate("/");
    },
  };

  return (
    <>
      {es.mode === "testing" && es.on_break ? (
        <BreakScreen es={es} breakLeft={breakLeft} timeLeft={timeLeft} onResume={resumeSection} />
      ) : view === "review" ? (
        <ReviewScreen
          es={es}
          onNavigate={(n) => goTo(n)}
          onStartWalk={startWalk}
          onOpenSubmit={() => setConfirmOpen(true)}
          onOpenEndSection={() => setEndSectionOpen(true)}
          onBack={() => setView("question")}
        />
      ) : es.mode === "testing" ? (
        <TestingView {...shared} />
      ) : (
        <StudyView {...shared} />
      )}

      {endSectionOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setEndSectionOpen(false)} />
          <div data-testid="end-section-confirm-modal" className="relative bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900">End Section {es.section}?</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              You have answered <strong>{Object.keys(es.answers).filter((k) => Number(k) >= secLo && Number(k) <= secHi).length} of {secHi - secLo + 1}</strong> questions
              in this section. Once you end it, you <strong>cannot return to questions {secLo}–{secHi}</strong>.
              An optional 10-minute break follows with your exam clock paused.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                data-testid="cancel-end-section-btn"
                onClick={() => setEndSectionOpen(false)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Keep working
              </button>
              <button
                data-testid="confirm-end-section-btn"
                onClick={endSection}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors"
              >
                End Section {es.section}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setConfirmOpen(false)} />
          <div data-testid="submit-confirm-modal" className="relative bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900">
              {es.mode === "study" ? "Finish this study session?" : "Submit your exam?"}
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              You have answered <strong>{answeredCount} of {es.total_questions}</strong> questions.
              {answeredCount < es.total_questions && (
                <> Unanswered questions will be scored as incorrect.</>
              )}{" "}
              This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                data-testid="cancel-submit-btn"
                onClick={() => setConfirmOpen(false)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Keep working
              </button>
              <button
                data-testid="confirm-submit-btn"
                disabled={submitting}
                onClick={() => doSubmit(false)}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white transition-colors"
              >
                {submitting ? "Submitting…" : es.mode === "study" ? "Finish and Score" : "Submit Exam"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
